// Faction palette — keyed by the engine's plural names (humans,
// goblins, ...). Every color is an Endesga-32 entry, picked for
// visual identity rather than nearest-neighbor (e.g. elves snap to
// mulberry, not the closer-but-grey steel, so they're still
// recognizably the purple faction).

import { E32 } from "./endesga32";

export interface Faction {
  key: string;
  /** Display name, e.g. "Human" (design uses singular). */
  name: string;
  /** Capital-city / region flavor label shown under the player name. */
  hue: string;
  color: string;
  glow: string;
  /** Ink color that reads well on top of `color`. */
  ink: string;
  /** URL of the white-on-transparent pixel sigil. Rendered as a CSS
   *  mask-image so any color works (see <Sigil/>). */
  sigil: string;
  /** Default sigil color on a parchment-faced surface. Mostly the
   *  faction color; humans fall back to ink-black because the human
   *  faction color is white. */
  sigilInk: string;
}

export const FACTIONS: Record<string, Faction> = {
  humans: {
    key: "humans",
    name: "Human",
    hue: "Antar",
    color: E32.white,
    glow: "rgba(255, 255, 255, 0.55)",
    ink: E32.ink,
    sigil: "/sigils/humans.png",
    sigilInk: E32.ink,
  },
  goblins: {
    key: "goblins",
    name: "Goblin",
    hue: "Clanktown",
    color: E32.scarlet,
    glow: "rgba(228, 59, 68, 0.55)",
    ink: E32.white,
    sigil: "/sigils/goblins.png",
    sigilInk: E32.scarlet,
  },
  dwarves: {
    key: "dwarves",
    name: "Dwarf",
    hue: "Khalami",
    color: E32.brick,
    glow: "rgba(184, 111, 80, 0.55)",
    ink: E32.white,
    sigil: "/sigils/dwarves.png",
    sigilInk: E32.brick,
  },
  orcs: {
    key: "orcs",
    name: "Orc",
    hue: "Vaguul",
    color: E32.fern,
    glow: "rgba(99, 199, 77, 0.55)",
    ink: E32.ink,
    sigil: "/sigils/orcs.png",
    sigilInk: E32.fern,
  },
  elves: {
    key: "elves",
    name: "Elf",
    hue: "Aramora",
    color: E32.mulberry,
    glow: "rgba(104, 56, 108, 0.55)",
    ink: E32.white,
    sigil: "/sigils/elves.png",
    sigilInk: E32.mulberry,
  },
};

/** URL of the brand-ornament pixel sigil (the "◆" handle). Rendered
 *  through <Sigil src=…/> as a recolorable mask, same as the faction
 *  sigils. */
export const ORNAMENT_SIGIL = "/sigils/ornament.png";

/** Look up a faction by key. Throws on unknown or missing keys.
 *
 *  There is no neutral faction in this game. Runtime-spawned troops
 *  (Troop a 0/5 Bone, Troop a 15/15 Skeleton, …) have no CardDef of
 *  their own; the engine snapshot fills their `faction` field with
 *  the controller's seat faction (see
 *  `engine2/ui/snapshot.rs::infer_seat_faction`) so the wire never
 *  carries a null faction for a real card. Anything that lands here
 *  with null IS a bug, and throwing surfaces it immediately. */
export function factionFor(key: string | null | undefined): Faction {
  if (key && FACTIONS[key]) return FACTIONS[key];
  // Ad-hoc troops summoned by Lua (e.g. Feral Tinker → Clockwork
  // Minion) have no `CardDef` in the engine's database, so the wire
  // ships `faction: null` for them. Crashing the whole battle on a
  // missing-faction token is way out of proportion to the cosmetic
  // problem; render with a neutral chrome instead.
  return NEUTRAL_FACTION;
}

const NEUTRAL_FACTION: Faction = {
  key: "neutral",
  name: "Neutral",
  hue: "—",
  color: E32.slate,
  glow: "rgba(180, 180, 180, 0.55)",
  ink: E32.white,
  sigil: "/sigils/goblins.png",
  sigilInk: E32.slate,
};

/** Prettify an engine `world` tag (e.g. `high_fantasia` → `High Fantasia`). */
export function worldLabel(world: string | null | undefined): string {
  if (!world) return "";
  return world
    .split("_")
    .map((s) => (s.length ? s[0].toUpperCase() + s.slice(1) : s))
    .join(" ");
}
